import React, { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Heading,
  Icon,
  Text,
  Button,
  Badge,
} from '@chakra-ui/react'
import {
  FiHome,
  FiActivity,
  FiUsers,
  FiDollarSign,
  FiAlertCircle,
} from 'react-icons/fi'
import { apiConfig } from '../../config/api'

interface NavItem {
  path: string
  label: string
  icon: any
  badge?: number
}

export const AdminLayout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [metaErrorCount, setMetaErrorCount] = useState(0)

  // Fetch meta error count on mount
  useEffect(() => {
    const fetchErrorCount = async () => {
      const token = localStorage.getItem('firebase_id_token')
      if (!token) return

      try {
        const response = await fetch(`${apiConfig.baseURL}/api/v1/admin/overview`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })

        if (response.ok) {
          const data = await response.json()
          setMetaErrorCount(data.meta_errors?.total_failed || 0)
        }
      } catch (error) {
        console.error('Failed to fetch error count:', error)
      }
    }

    fetchErrorCount()
    // Refresh every 30 seconds
    const interval = setInterval(fetchErrorCount, 30000)
    return () => clearInterval(interval)
  }, [])

  const navItems: NavItem[] = [
    { path: '/admin/overview', label: 'Overview', icon: FiHome },
    { path: '/admin/campaigns', label: 'Campaigns', icon: FiActivity },
    { path: '/admin/practitioners', label: 'Practitioners', icon: FiUsers },
    { path: '/admin/transactions', label: 'Transactions', icon: FiDollarSign },
  ]

  const isActive = (path: string) => {
    if (path === '/admin/overview') {
      return location.pathname === '/admin' || location.pathname === '/admin/overview'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <Box minH="100vh" bg="gray.50" display="flex" flexDirection="column">
      {/* Header */}
      <Box bg="white" borderBottom="1px" borderColor="gray.200" shadow="sm">
        <Flex h={16} px={6} align="center" justify="space-between">
          <Heading size="lg" color="gray.900">
            Spotlight Admin
          </Heading>
          <HStack gap={4}>
            {metaErrorCount > 0 && (
              <Badge colorPalette="red" fontSize="sm" px={2} py={1}>
                <HStack gap={1}>
                  <Icon as={FiAlertCircle} />
                  <Text>{metaErrorCount} Meta Errors</Text>
                </HStack>
              </Badge>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate('/')}
            >
              Back to Site
            </Button>
          </HStack>
        </Flex>
      </Box>

      <Flex flex={1} overflow="hidden">
        {/* Sidebar */}
        <Box
          w={64}
          bg="gray.900"
          color="white"
          py={6}
          borderRight="1px"
          borderColor="gray.700"
        >
          <VStack align="stretch" gap={1} px={4}>
            {navItems.map((item) => (
              <Button
                key={item.path}
                variant={isActive(item.path) ? 'solid' : 'ghost'}
                colorPalette={isActive(item.path) ? 'brand' : 'gray'}
                justifyContent="flex-start"
                leftIcon={<Icon as={item.icon} />}
                onClick={() => navigate(item.path)}
                position="relative"
              >
                {item.label}
                {item.badge && item.badge > 0 && (
                  <Badge
                    colorPalette="red"
                    position="absolute"
                    right={2}
                    fontSize="xs"
                  >
                    {item.badge}
                  </Badge>
                )}
              </Button>
            ))}
          </VStack>
        </Box>

        {/* Main Content */}
        <Box flex={1} overflowY="auto" p={8}>
          <Outlet />
        </Box>
      </Flex>
    </Box>
  )
}

